import { Box } from "@mui/material";
import ExistingProjectsListPopup from 'src/pages/dashboard/homePage/MarketplaceListing/ExistingProjectsListPopup';
import ImageUploadButton from "../../components/button/ButtonWithIconAndText";
import { completeStrapiData } from "src/strapiData";
import { dispatch, useSelector } from "src/redux/store";
import useAuth from '@/hooks/useAuth';
import { useDropzone } from "react-dropzone";
import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { uploadFile, createGoogleDriveJob } from "src/services/file-upload";
import GlobalAPI from '@/modules/batchEditor/services/GlobalAPI';
import { convertToCSV } from "./FolderUploadStep";
import { generateHash } from "src/utils/helpers";
import { segmentEventPayload, showLoginPopupAtom } from "src/modules/editor-v2/Atoms";
import { useSetAtom, useAtomValue } from "jotai";
import useToast from "src/hooks/useToast";
import { get, head, set } from "lodash";
import { Icon, ICONS } from "@/modules/ui-elements/Icon";
import { styles } from "./homepageUploadModuleStyle";
import CircularProgress from '@mui/material/CircularProgress';
import { useQuery } from "@tanstack/react-query";
import { TrackWebappEvent } from "src/modules/segment-event-tracking/segmentEventTracking";
import { EventTrackName } from "src/modules/segment-event-tracking/constant";
import { getDummyJobs } from "src/modules/editor-v2/services/Jobs";
import { aiTaskDisableAtom, batchEditorSelectedPresetAtom } from "../batchEditor/atoms";
import { EditorScreen } from "../batchEditor/BulkUploadEditor/constant";
import { useRouter } from "next/router";
import BatchEditorSuccessDialog from "./batchEditorSucessDialog";
import {  validateGoogleDriveLink } from "src/services/file-upload";
import CSVUploadJourney from './CSVUploadJourney';
import useBatchAIStore from "src/modules/batchEditor/BulkUploadNewStore/BulkAIStore";
import useBatchStore from "src/modules/batchEditor/BulkUploadNewStore/BulkUploadStore";
import { localConvertToCSV, removeExtension } from "src/utils/helpers";
import { createProject } from "src/services/file-upload";

export async function processCSV(csvText, fileName) {
  const rows = csvText.split('\n').filter(row => row.trim());

  const result = [];
  let imageURLcount = 0;
  let hasRestrictedLink = false; // Flag to track if there are restricted links
  let hasGoogleDriveFolderLink = false; // Flag to track if Google Drive folder links were encountered

  // Start from index 1 to skip the header row
  for (let i = 1; i < rows.length; i++) {
      // Split columns by comma and trim any whitespace
      const columns = rows[i].split(',').map(col => col.trim()); 
      // Skip rows with insufficient columns (if any)
      if (columns.length < 6) {
          // console.error(`Error: Missing mandatory columns in row ${i + 1}`);
          continue;  // Skip invalid rows
      }

      // Creating the object to hold the validated data
      const obj = {
          projectName: columns[0],     // //For csv we use sku_id as the joblabel
          skuId: columns[0],           // SKU ID
          height: columns[2],          // Height (px)
          width: columns[3],           // Width (px)
          position: columns[4],        // Product Position
          imageUrls: [],                // Image URLs will be populated below
          batchName: fileName
      };

      if (!obj.projectName) {
          console.error(`Project Name is missing or undefined in row ${i + 1}`);
      }

      let isValidRow = true; // Flag to check if the row is valid

      // Validate Image 1 (mandatory)
      const image1 = columns[5];
      if (!image1) {
          Toast.error(`Image 1 is required at row ${i + 1}`);
          isValidRow = false; // Mark the row as invalid
      } else {
          const isValidImage1Url = await validateImageUrl(image1, i, 5);
          if (!isValidImage1Url) {
              isValidRow = false;  // Mark the row as invalid
          } else {
              // If Image 1 is valid, push to the imageUrls array
              obj.imageUrls.push({ dataURL: image1 });
              imageURLcount += 1;
          }
      }

      // Validate Image 2 and beyond (optional)
      for (let j = 6; j < columns.length; j++) {
          const imageUrl = columns[j];
          if (imageUrl) {
              const isValid = await validateImageUrl(imageUrl, i, j);
              if (isValid) {
                  obj.imageUrls.push({ dataURL: imageUrl });
                  imageURLcount += 1;
              }
          }
      }

      // Check if any URL contains a Google Drive folder link, skip the row if it does
      for (let j = 5; j < columns.length; j++) {
          const imageUrl = columns[j];
          if (imageUrl && imageUrl.includes('drive.google.com/drive/folders')) {
              if (!hasGoogleDriveFolderLink) {
                  Toast.error(`Google Drive folder link not allowed at Image ${j - 4} in row ${i + 1}`);
                  hasGoogleDriveFolderLink = true; // Set the videoFormatFlag to true
              }
              isValidRow = false; // Mark the row as invalid
              break;  // Stop processing this row
          }
      }

      // Check URL accessibility only for Google Drive file URLs
      if (isValidRow) {
          for (let j = 5; j < columns.length; j++) {
              const imageUrl = columns[j];
              if (imageUrl) {
                  // Only check the accessibility of Google Drive file links
                  if (imageUrl.includes('drive.google.com/file/d/')) {
                      const isAccessible = await isGoogleDriveFileAccessible(imageUrl);
                      if (!isAccessible) {
                          if (!hasRestrictedLink) {
                              Toast.error("This CSV contains restricted links. Some files may be missing.");
                              hasRestrictedLink = true; // Set the videoFormatFlag to true
                          }
                          isValidRow = false;  // Mark the row as invalid
                          break;  // Stop processing this row
                      }
                  }
              }
          }
      }

      // Only add valid rows to the result array
      if (isValidRow) {
          result.push(obj);
      }
  }

  // If no valid rows exist after processing, show a message and do not proceed with API call
  if (result.length === 0) {
      Toast.error("Restricted link or folder link not allowed in CSV.");
      return [];  // Return empty to indicate no rows to process
  }

  // Store results and proceed to API upload if there are valid rows
  sessionStorage.setItem('batchCSVData', JSON.stringify(result));
  sessionStorage.setItem('imageUrls', imageURLcount);

  return result;  // Return the result only if validation passed
}


// Helper function to validate the image URL
async function validateImageUrl(imageUrl, rowIndex, columnIndex) {
  // Check if the URL is a Google Drive file link
  if (imageUrl.includes('drive.google.com/file/d/')) {
      const isValidDriveUrl = await isGoogleDriveFileAccessible(imageUrl, rowIndex, columnIndex);
      if (!isValidDriveUrl) {
          console.error(`Error: Google Drive file is not accessible at Image ${columnIndex - 4} in row ${rowIndex + 1}`);
          return false;  // Invalid if Google Drive file is not accessible
      }
      return true; // Valid Google Drive file URL
  }

  const isValidUrl = /^https?:\/\/[^\s/$.?#].[^\s]*$/.test(imageUrl); // Basic URL regex check
  if (!isValidUrl) {
      console.error(`Invalid URL at Image ${columnIndex - 4} in row ${rowIndex + 1}: ${imageUrl}`);
      return false;  // Invalid URL if it doesn't match the basic format
  }

  // console.log(`Basic URL validation passed for Image ${columnIndex - 4} in row ${rowIndex + 1}`);
  return true; 
}

// Function to check if a Google Drive file link is accessible
async function isGoogleDriveFileAccessible(url) {
  try {
      // Attempt to access the Google Drive file by sending a HEAD request
      const response = await fetch(url, { method: 'HEAD' });

      if (response.ok) {
          return true; // Google Drive file is accessible
      } else {
          console.error(`Google Drive file is not accessible. Response: ${response.status}`);
          return false; // Google Drive file is not accessible
      }
  } catch (error) {
      console.error(`Error accessing Google Drive file: ${error}`);
      return false; // Error while trying to access the URL
  }
}import { resizeAndCompressImage } from "@/utils/imageCompression";
import SelectGenerationModel from "./SelectGenerationModel";
import { hasLongImageUrl, hasMoreImageUrls, containsVideoLink } from "./CsvFileUploadStep";
import { reset, updateStep, updateTitle } from "@/redux/reducers/file-upload.reducer";
import { STEPS } from "@/utils/constant";

const HomepageUpload = ({ isBulkAIType, onCloseButtonClick=()=>{}, initiatedSource, marketPlaceIntiatedSource, onUploadSuccess = async () => { }, onUploadFailure = (error) => { }, onGoogleDriveUpload, aiTaskDisable}) => {
  const { user } = useAuth();
  const router = useRouter();
  const fileUpload = useSelector((state) => state.fileUpload);
  const { entireStrapiData } = useSelector((state) => state.strapi);
  const CreateProjectData = entireStrapiData?.StudioBatchEditorText?.StudioBatchEditorText;
  const defaultStrapiData = completeStrapiData?.CreateProjectWebApp?.CreateProjectWebApp;
  const [error, setError] = useState();
  const isExternalUser = useMemo(()=>isBulkAIType && !user?.user?.infoGraphicsUser,[isBulkAIType,user]);
  const dropzoneRef = useRef(null);
  const limitValue = aiTaskDisable ? (process.env.NEXT_PUBLIC_MAX_CSV_ROW_IMAGE_LIMIT || 100) : (process.env.NEXT_PUBLIC_MAX_AI_TASK_ENABLE_FILE_LIMIT || 20);
//Not reusing the NEXT_PUBLIC_BATCH_JOB_LIMIT as the limit has been decreased which will cause error for previous jobs with more than 20 images
  const trackRef = useRef(false);
  const Toast = useToast();
  const [csvFile, setCSVFile] = useState();
  const setLoginPopupAtom = useSetAtom(showLoginPopupAtom);
  const getSegmentPayloadAtom = useAtomValue(segmentEventPayload);
  const guestUser = user?.user?.login_type === "Guest";
  const uploadInputEl = useRef(null);
  const [folderData, setFolderData] = useState({});
  const [folderLength, setFolderLength] = useState(Number)
  const [batchName, setBatchName] = useState('');
  const [openExistingProjects, setOpenExistingProjects] = useState(false)
  const [isLoading, setLoading] = useState(false);
  const [isRedirectUrl, setRedirectUrl] = useState(false);
  const [csvUploading, setCsvUploading] = useState(false);
  const [currentPayload,setCurrentPayload] = useState(null);
  const [removeBackground,setRemoveBackground] = useState(false);
  const [openSelectionDialog, setOpenSelectionDialog] = useState(false);
  const batchEditorSelectedPreset = useAtomValue(batchEditorSelectedPresetAtom);
  const customInitatedSource = !(marketPlaceIntiatedSource || initiatedSource) ? 'marketplace' : '' ;
  const [openFileUploadSuccessDialog, setOpenFileUploadSuccessDialog] = useState(false);
  const [batchCSVUploadJourney,setBatchCSVUploadJourney] = useState(false);
  const {userTrackInfoPayload}=useSelector((state)=>state.jobs);
  const {
    clearBulkAIStore
  } = useBatchAIStore();


  const {resetBulkUploadStore} = useBatchStore()
  const {data:dummyJobs, isError, isLoading:dummyJobsLoading} = useQuery({
    queryFn: getDummyJobs,
    queryKey: ["dummyJobs"]
});

useEffect(()=>{
  clearBulkAIStore();
  resetBulkUploadStore();
},[])

  useEffect(() => {
    if (folderData.length == folderLength) {
      folderConversion(folderData)
    }
  }, [folderData]);

  const folderConversion = async (folderData) => {
    try{
      const csvData = await convertToCSV(folderData);
      const blob = new Blob([csvData], { type: 'text/csv' });
      const imageLimitCrossed = await hasLongImageUrl(blob);
      if(imageLimitCrossed){
        Toast.info("Max image limit exceeded. Remove images.");
        dispatch(reset());
        dispatch(updateTitle("Create Project"));
        dispatch(updateStep(STEPS?.INIT));
        return;
      }
      const formData = new FormData();
      const fileName = batchName || 'batch';
      const file = new File([blob], fileName, { type: 'text/csv' });
      formData.append("file", file);
      formData.append("uploadType", "csv");
      formData.append("initiatedSource", customInitatedSource ? customInitatedSource : initiatedSource)
      marketPlaceIntiatedSource && formData.append("marketplaceId", marketPlaceIntiatedSource)
      !aiTaskDisable && formData.append("priorityValue",1);
      await initateUpload(formData)
    }
    catch(error){
      Toast.error("Something went wrong")
    }
  }

  const clearInputElValue = () => {
    if (!uploadInputEl.current) return;
    uploadInputEl.current.value = null;
  };
  
  const getFileS3Url = async (file, folderName, id, options) => {
    try {
      setLoading(true)
      const filePath = `${folderName}/${file.name}`;
      const response = await uploadFile(file, options, filePath);
      if (get(response, "data.success", false)) {
        const imageUrl = head(get(response, "data.images", []));
        return imageUrl;
      } else {
        // handleFileUploadFailCase(file, name, id);
      }
      return false;
    } catch (error) {
      Toast.error(error?.response?.data?.data);
      console.error(error);
    }
  };

  //Initate batch job
  async function initateUpload(formData) {
    try {
      
      const response = await GlobalAPI.uploadBulkJobCSV(formData);
      const batchId = response.data?.data?.batchId
      if(isBulkAIType){
        window.location.href = `${window.location.origin}/dashboard/modelAI?batchId=${batchId}`;
        setRedirectUrl(true);
        await new Promise(res=>setTimeout(res,3000));
        setRedirectUrl(false);
        return;
      }
      if (batchId) {
        await onUploadSuccess({ batchId, ...(marketPlaceIntiatedSource && { marketPlaceIntiatedSource }), ...(initiatedSource && {initiatedSource}) });
        // await pollData(response.data?.data?.batchId);
      } else {
        Toast.error(CreateProjectData?.primaryErrorText || "Something went wrong");
      }
    } catch (error) {
      const message = error.response?.data?.message?.csvError || error.response?.data?.errorResponse || 'CSV upload failed';
      // Toast.error(message, "error", error);
      //onUploadFailure?.(error);
      console.log("error", error)
    } finally {
      setLoading(false)
    }
  }


  //payload for batchUpload
  const uploadCSVpayload = async (imageUrls, acceptedFiles=[], ExistingProjectZerothJobName="") => {
    try {
      if(imageUrls?.length < 1)
      {
        Toast.error(CreateProjectData?.fileUploadLimt ||"No files uploaded")
        return;
      }
      if(imageUrls?.length > limitValue)
      {
        Toast.error(`${CreateProjectData?.fileUploadLimit || "Upload upto"} ${limitValue} ${CreateProjectData?.ImagesText|| "images"}` ||`$Upload upto ${limitValue} images`)
        return;
      }
      
      sessionStorage.setItem('imageUrls', imageUrls?.length)
      setLoading(true)
      const fileName = ExistingProjectZerothJobName || removeExtension(acceptedFiles[0]?.path);
      const payload = [{
        imageUrls: imageUrls.map(image => ({ dataURL: image })),
        name: fileName,
        batchName:fileName
      }];

      const csvData =await convertToCSV(payload);
      const blob = new Blob([csvData], { type: 'text/csv' });
      const file = new File([blob], fileName, { type: 'text/csv' });
      const hasVideoLink = await containsVideoLink(file);
      if(hasVideoLink && !aiTaskDisable){
        Toast.info("Cannot process CSV with video links. Please remove them!");
        dispatch(reset());
        dispatch(updateTitle("Create Project"));
        dispatch(updateStep(STEPS?.INIT));
        return;
      }
      const imageLimitCrossed = await hasLongImageUrl(blob);
      if(imageLimitCrossed){
        Toast.info("Max image limit exceeded. Remove images.");
        dispatch(reset());
        dispatch(updateTitle("Create Project"));
        dispatch(updateStep(STEPS?.INIT));
        return;
      }
      const formData = new FormData();
      formData.append("file", file);
      formData.append("uploadType", "csv");
      formData.append("initiatedSource", customInitatedSource ? customInitatedSource : initiatedSource)
      marketPlaceIntiatedSource && formData.append("marketplaceId", marketPlaceIntiatedSource) 
      if(!ExistingProjectZerothJobName && !aiTaskDisable)formData.append("priorityValue",1);
      await initateUpload(formData)
    }
    catch (e) {
      console.error(e);
    }
  }

  // Check for Video files upload only for AiTaskDisable Presets
   async function checkForFilesAndSelecedTool(initiatedSource, aiTaskDisable, file) {
     const videoAcceptedExtensions = [".mp4", ".mkv", ".webm", ".mov", ".m4v", ".avi", ".flv"];
     const imageAcceptedExtensions = [".tiff", ".tif" , ".cr2", ".psd", ".nef", ".arw", ".dng" , ".cr3"];
     const webpExtension = ".webp";
     const fileName = file?.name || file?.path;
     const extension = '.' + fileName.split('.').pop().toLowerCase();
    if (initiatedSource==='marketplace') {
      if (!aiTaskDisable) {
        let errorMsg = "";
        if ((videoAcceptedExtensions.includes(extension)) || 
        (imageAcceptedExtensions.includes(extension) && extension !== webpExtension)) {
      Toast.error(`Cannot upload ${videoAcceptedExtensions.includes(extension) ? 'video' : 'image'} files for ${extension} format`);
      setLoading(false);
      return false;
    }

        return true;
      }
      else {
        const maxSize = process.env.NEXT_PUBLIC_BULK_VIDEO_FILE_LIMIT * 1024 * 1024;
        if (file && videoAcceptedExtensions.includes(extension)) {
          if (file.size > maxSize) {
            Toast.error(`Video File size exceeds the maximum allowed size of ${Math.floor(process.env.NEXT_PUBLIC_BULK_VIDEO_FILE_LIMIT)}mb!`);
            setLoading(false);
            return false;
          }
          sessionStorage.setItem('videoUploadOnAiTaskDisable', true);
        }
        return true;
      }
    } else {
      if (file) {
        if (videoAcceptedExtensions.includes(extension)) {
          Toast.error(`Cannot upload video files for ${extension} format`);
          setLoading(false);
          return false;
        }
        if (imageAcceptedExtensions.includes(extension) && extension !== webpExtension) {
          Toast.error(`Cannot upload image file for ${extension} format`)
          setLoading(false);
          return false;
        }
      }
    }
    return true;
  }

  // Parallel Upload
  function uploadFilesParallelly(files) {
    const promises = files.map(async (file) => {
      try {
        const fileFormatFlag = await checkForFilesAndSelecedTool(initiatedSource, aiTaskDisable, file);
        if (!fileFormatFlag) return { status: 'rejected', reason: 'File format invalid or tool not selected' };
  
        let updatedFile = file;
        if (!aiTaskDisable) updatedFile = await resizeAndCompressImage(file);
  
        const response = await uploadFile(updatedFile);
        return { status: 'fulfilled', value: response };
      } catch (error) {
        setLoading(false);
        Toast.error(error?.response?.data?.data);
        return { status: 'rejected', reason: error };
      }
    });
  
    return Promise.all(promises);
  }  

  //Folder Upload 
  const onFolderSelectBulkUpload = async (e) => {
    if(isExternalUser)return;
    try {
      TrackWebappEvent(`${process.env.NEXT_PUBLIC_EVENT_TRACK_TYPE}${EventTrackName._Click_Upload_Folders}`, {...getSegmentPayloadAtom});
      let filesSelected = e?.target?.files;
      if (!filesSelected.length) {
        Toast.error("Folder is empty");
        return;
      }
      if(filesSelected?.length > limitValue)
      {
        Toast.error(`${CreateProjectData?.fileUploadLimit || "Upload upto "} ${limitValue} ${CreateProjectData?.ImagesText|| "images"}` ||`$Upload upto ${limitValue} images`)
        return;
      }
      sessionStorage.setItem('imageUrls', filesSelected?.length)
      const folderMap = {};
      const folderListMap = {};
      let subfolderPath = "";

      for (let file in filesSelected) {
        if (filesSelected.hasOwnProperty(file)) {
          const selectedFile = filesSelected[file];
          const fileName = selectedFile.name;
          
          if (fileName.startsWith('.') || fileName === '.DS_Store') {
            continue;
          }
          subfolderPath = filesSelected[file].webkitRelativePath.split('/');
          if (subfolderPath.length > 2)setBatchName(subfolderPath[0]);
          else setBatchName(`Batch_${subfolderPath[0]}`);
          let subfolderName;
          if (subfolderPath.length > 2) {
             subfolderName = subfolderPath[1];
          }
          else{
            subfolderName = subfolderPath[0];
          }
          if (!folderListMap[subfolderName]) {
            folderListMap[subfolderName] = [];
          }
          folderListMap[subfolderName].push(filesSelected[file]);
        }
      }
      setFolderLength(Object.keys(folderListMap).length)
      for (const item in folderListMap) {
        const folderName = item;
        const subId = generateHash(folderName);
        if (!folderMap[folderName]) {
          folderMap[folderName] = [];
        }
        for (let i = 0; i < folderListMap[item].length; i++) {
          try {
            const file = folderListMap[item][i];
            const fileFormatFlag = await checkForFilesAndSelecedTool(initiatedSource, aiTaskDisable, file);
            if (!fileFormatFlag) return;
            
            let updatedFile = file;
            if (!aiTaskDisable) updatedFile = await resizeAndCompressImage(file);
            
            const url = await getFileS3Url(updatedFile, folderName, subId);
            if (url) {
              const imageData = {
                dataURL: url
              };
              if (!folderMap[folderName]) {
                folderMap[folderName] = [];
              }
              folderMap[folderName].push(imageData);
            }
          } catch (error) {
            setLoading(false);
            // onUploadFailure?.(error);
              // Toast.error(`${CreateProjectData?.errorInProcessing || "Error processing file"} ${folderListMap[item][i]}:`);
              console.error(`Error processing file ${folderListMap[item][i]}:`, error);
          }
        }
        if(isBulkAIType){
          const payload = [{ name: item, imageUrls: folderMap[item] , batchName:subfolderPath[0]|| 'batch'}];
          setCurrentPayload(payload);
          setOpenFileUploadSuccessDialog(true);
        }else{
        setFolderData(prev => {
          const prevFolderData = Array.isArray(prev) ? prev : [];
          const filteredList = prevFolderData.filter(e => e.name !== item);
          return [...filteredList, { name: item, imageUrls: folderMap[item] , batchName:subfolderPath[0]|| 'batch'}];
        });
      }
      }
    } catch (error) {
      console.error(error);
    } finally {
      clearInputElValue();
    }
  };

  //Projects Upload
  const batchEditorJobFromExistingImages = (imageUrls, ExistingProjectZerothJobName="") => {
    setOpenExistingProjects(false)
    TrackWebappEvent(`${process.env.NEXT_PUBLIC_EVENT_TRACK_TYPE}${EventTrackName._Click_Next_Upload_Existing_Project}`, {...getSegmentPayloadAtom});
    uploadCSVpayload(imageUrls, [], ExistingProjectZerothJobName)
  }

  const singleJobFromExistingImages = async (imageUrls, ExistingProjectZerothJobName = "") => {
    setOpenExistingProjects(false)
    TrackWebappEvent(`${process.env.NEXT_PUBLIC_EVENT_TRACK_TYPE}${EventTrackName._Click_Next_Upload_Existing_Project}`, {...getSegmentPayloadAtom});
    const response = await reuploadImageFromExistingImages(imageUrls)
    singleJobFlow(response, ExistingProjectZerothJobName)
  }

  const reuploadImageFromExistingImages = async (imageUrls) => {
    try {
        setLoading(true)
        setOpenExistingProjects(false);
        TrackWebappEvent(`${process.env.NEXT_PUBLIC_EVENT_TRACK_TYPE}${EventTrackName._Click_Next_Upload_Existing_Project}`, {...getSegmentPayloadAtom});
        
        const uploadPromises = imageUrls.map(async (url, i) => {
            const resp = await fetch(url);
            if (!resp.ok) {
                throw new Error('Failed to download image');
            }
            const blob = await resp.blob();
            const filename = `existingProject_${i}` + ".jpeg";
            const file = new File([blob], filename);
            return file;
        });

        const files = await Promise.all(uploadPromises);
        const responses = await uploadFilesParallelly(files);
        
        const successfulUploads = responses
            .filter(response => response.status === 'fulfilled')
            .flatMap(response => response.value.data.images);
        
        return successfulUploads;
    } catch (error) {
        setLoading(false)
        console.error('Error in reuploadImageFromExistingImages:', error);
        throw error;
    }
  };

// Callback for handling the CSV file drop and initiating the process
const onDropCSV = useCallback(
  async (acceptedFiles) => {
      TrackWebappEvent(`${process.env.NEXT_PUBLIC_EVENT_TRACK_TYPE}${EventTrackName._Click_Upload_CSV}`, {...getSegmentPayloadAtom});

      if (!acceptedFiles?.length) return;
  
      // Check if only one CSV is uploaded
      if (acceptedFiles.length !== 1) {
          setError(CreateProjectData?.csvUploadLimit || "Please upload exactly one CSV file");
          return;
      } else {
          setError(null);
      }
  
      // Prepare the file
      const blob = new Blob([acceptedFiles[0]], { type: 'text/csv' });
      const fileName = removeExtension(acceptedFiles[0]?.path);
      const file = new File([blob], fileName, { type: 'text/csv' });
      const hasVideoLink = await containsVideoLink(file);
      if(hasVideoLink && !aiTaskDisable){
        Toast.info("Cannot process CSV with video links. Please remove them!");
        dispatch(reset());
        dispatch(updateTitle("Create Project"));
        dispatch(updateStep(STEPS?.INIT));
        return;
      }
    if(aiTaskDisable || isBulkAIType){
      const imageLimitCrossed = await hasLongImageUrl(file);
      if(imageLimitCrossed){
        Toast.info("Max image limit exceeded. Remove images.");
        dispatch(reset());
        dispatch(updateTitle("Create Project"));
        dispatch(updateStep(STEPS?.INIT));
        return;
      }
    }else{
      const maxImageLimitCrossed = await hasMoreImageUrls(file);
      if(maxImageLimitCrossed){
        Toast.info(maxImageLimitCrossed);
        dispatch(reset());
        dispatch(updateTitle("Create Project"));
        dispatch(updateStep(STEPS?.INIT));
        return;
      }
    }

      const formData = new FormData();
      const reader = new FileReader();
  
      // On file read success
      reader.onload = async function(event) {
          const csvContent = event.target.result;
  
          // Process the CSV and validate
          const jsonOutput = await processCSV(csvContent,fileName);
          if (!jsonOutput || jsonOutput.length === 0) {
              // Stop further processing if CSV validation fails
              return;
          }
  
          // Convert the processed data (JSON) back to CSV format
          const processedCSV = localConvertToCSV(jsonOutput);
  
          formData.append("file", new Blob([processedCSV], { type: 'text/csv' }), fileName);
          formData.append("uploadType", "csv");
          formData.append("initiatedSource", customInitatedSource ? customInitatedSource : initiatedSource);
          marketPlaceIntiatedSource && formData.append("marketplaceId", marketPlaceIntiatedSource);
          try {
              setLoading(true);
              await initateUpload(formData);  
          } catch (e) {
              console.error("error", e);
          } finally {
              setLoading(false);
          }
      };
  
      reader.onerror = function(event) {
          console.error("File could not be read! Code " + event.target.error.code);
      };
  
      reader.readAsText(file);
      if (dropzoneRef.current) {
        dropzoneRef.current.value = '';
      }
  },
  [setError]
);

  const onExistingProjectHandler = (payload) => {
    setCurrentPayload(payload);
    setOpenFileUploadSuccessDialog(true);
  };

  const { getInputProps: getInputPropsCSV, getRootProps: getRootPropsCSV } = useDropzone({
    onDrop: onDropCSV,
    accept: { "text/csv": [".csv"] },
  });

  const singleJobFlow = async(imageUrls, acceptedFiles)=>{
    try{
          const payload = {
            imageURLs: imageUrls,
            name: removeExtension(acceptedFiles[0]?.path) || "imageAI",
            asyncJob: true,
            batchEditorInitiatedSource: customInitatedSource || initiatedSource, 
            ...(marketPlaceIntiatedSource && { selectedpreset: marketPlaceIntiatedSource }), 
          };
          const response = await createProject(payload)
          const jobId = response?.data?.data?.jobId;
          const folderList = {
            imageUrls: imageUrls.map(image => ({ dataURL: image })),
            name: removeExtension(acceptedFiles[0]?.path)
          }
          sessionStorage.setItem('batchCSVData', JSON.stringify(folderList));
          await onUploadSuccess({ jobId, ...(marketPlaceIntiatedSource && { marketPlaceIntiatedSource }), ...(initiatedSource && {initiatedSource}) });     
    }
    catch(e){
      console.error("Error in job creation",e)
    }
    finally{
      setLoading(false);
    }
  }

  //File Upload
  const onDrop = useCallback(
    async (acceptedFiles) => {
      let hasVideoLink=false;
      if (!acceptedFiles?.length) return;
      if(isBulkAIType){
        try {
          setLoading(true);
          const eventPayload = {
            homePageFeature: fileUpload?.bulkUplodAIType == 'BULK_UPLOAD_GENAI' ? 'product in context'
            : fileUpload?.bulkUplodAIType == 'BULK_UPLOAD_FACE_SWAP' ? 'model swap'
            : 'model generation',
            ...userTrackInfoPayload
          }
          TrackWebappEvent(`${process.env.NEXT_PUBLIC_EVENT_TRACK_TYPE}${EventTrackName._Click_Upload_Files}`,eventPayload);
          const responses = await uploadFilesParallelly(acceptedFiles);
          if (responses.some(response => response.status === 'fulfilled')) {
            const imageUrls = responses
              .filter(response => response.status === 'fulfilled')
              .flatMap(response => response.value.data.images);
              const payload = [{
                imageUrls: Object.values(imageUrls).map(
                  (file,index) => ({dataURL:file})
                ),
                name: "imageAI",
              }];
              setCurrentPayload(payload);
              setOpenFileUploadSuccessDialog(true);
          } else {
            setError(CreateProjectData?.allfFileUploadFailure || "All file uploads failed.");
          }
        } catch (error) {
          setError(CreateProjectData?.csvUploadLimit || "Please upload exactly one CSV file");
        }
        return;
      }
      if(acceptedFiles?.length > limitValue)
        {
          Toast.error(`${CreateProjectData?.fileUploadLimit || "Upload upto"} ${limitValue} ${CreateProjectData?.ImagesText|| "images"}` ||`$Upload upto ${limitValue} images`)
          return;
        }
        sessionStorage.setItem('imageUrls', acceptedFiles?.length)
      try {
        TrackWebappEvent(`${process.env.NEXT_PUBLIC_EVENT_TRACK_TYPE}${EventTrackName._Click_Upload_Files}`, {...getSegmentPayloadAtom});
        setLoading(true)
        const responses = await uploadFilesParallelly(acceptedFiles);
        if (responses.some(response => response.status === 'fulfilled')) {
          const imageUrls = responses
            .filter(response => response.status === 'fulfilled')
            .flatMap(response => response.value.data.images);
            const blob = new Blob([acceptedFiles[0]], { type: 'text/csv' });
            const fileName = removeExtension(acceptedFiles[0]?.path);
            const file = new File([blob], fileName, { type: 'text/csv' });
            hasVideoLink = await containsVideoLink(file);
            if(hasVideoLink || aiTaskDisable)
            {
              uploadCSVpayload(imageUrls, acceptedFiles)
            } 
            else{
              singleJobFlow(imageUrls, acceptedFiles)
            }
        } else {
          setError(CreateProjectData?.allfFileUploadFailure || "All file uploads failed.");
        }
      } catch (error) {
        setError(CreateProjectData?.csvUploadLimit || "Please upload exactly one CSV file");
        setLoading(false);
      } finally {
        hasVideoLink && setLoading(false);
      }
    },
    [uploadFile, setError]
  );

  const selectionOptionHandler = (value) => {
    setRemoveBackground(value);
    if(isExternalUser)return saveProject("",value);
    setOpenSelectionDialog(true);
  };

  const saveProject = async(selectionData,bgRemove=false) => {
    try{
      if(!currentPayload)return;
     setCsvUploading(true);
     setRedirectUrl(true);
     const csvData = await convertToCSV(currentPayload);
              // Create a Blob object from the CSV data
      const blob = new Blob([csvData], { type: 'text/csv' });
      const formData = new FormData();
      formData.append("file", blob);
      formData.append("uploadType", "csv");
      // csvInstructionFile && formData.append("instructionFile", csvInstructionFile);
      formData.append("removeBackground",removeBackground || bgRemove);
      selectionData?.modelSelection && formData.append("modelSelection",JSON.stringify(selectionData?.modelSelection));
      formData.append("marketplaceId",batchEditorSelectedPreset?.marketplace_id);
      fileUpload?.bulkUplodAIType && formData.append("batchType",fileUpload.bulkUplodAIType);
      if (!aiTaskDisable) {
        formData.append("priorityValue", 1);
      }
      await GlobalAPI.uploadBulkJobCSV(formData)
        .then(async(response) => {
          if(response.data?.data?.batchId){
            let mode = EditorScreen.MAIN;
            if (aiTaskDisable) {
              mode = EditorScreen.PRODUCT_LIFESTYLE;
              router.replace({
                query: { mode, batchAI: response.data?.data?.batchId},
              });
            } else {
              window.location.href = `${window.location.origin}/dashboard/modelAI?batchId=${response.data?.data?.batchId}`;
              setTimeout(()=>{
                setCsvUploading(false);
              },3000)
            }
            await new Promise(res => setTimeout(res, 3000));
            onCloseButtonClick();
        }else{
          Toast.error("Something went wrong")
        }
        setCsvUploading(false);
        })
        .catch(error=>{
          const message = error.response.data?.message?.csvError || error.response.data?.errorResponse || 'CSV upload failed'
          Toast.error(message)
          setCsvUploading(false);
        })
        await new Promise(res=>setTimeout(res,5000));
        setRedirectUrl(false);
      }catch(err){
        setCsvUploading(false);
        console.error(err)
        setRedirectUrl(false);
      }
  };

  const handleGoogleDriveUpload = async ()=>{
    TrackWebappEvent(`${process.env.NEXT_PUBLIC_EVENT_TRACK_TYPE}${EventTrackName._Click_Upload_GD}`, {...getSegmentPayloadAtom});
    await onGoogleDriveUpload();
  }

  const downloadSampleCSV = () => {
    if(isExternalUser)return;
    const link = document.createElement('a');
    link.href = process.env.NEXT_PUBLIC_BULK_UPLOAD_INSTRUCTION_CSV_URL;
    link.download = 'BulkUploadSample.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleButtonClick = () => {
    if(isExternalUser)return;
    if (uploadInputEl.current) {
      uploadInputEl.current.click();
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "image/*": [".png", ".gif", ".jpeg", ".jpg", ".webp", ".tiff", ".tif", ".cr2", ".psd", ".nef", ".arw", ".dng", ".cr3"],
      ...(aiTaskDisable ? {
        "video/*": [".mp4", ".mkv", ".webm", ".mov", ".m4v", ".avi", ".flv"],
      } : {}),
    },
    multiple: !guestUser && !isExternalUser
  });  

  //dummy Upload
  const dummyJobUpload = async (name, bannerImage, singleJob)=>{
    TrackWebappEvent(`${process.env.NEXT_PUBLIC_EVENT_TRACK_TYPE}${EventTrackName._Click_Upload_sample_product}`, {...getSegmentPayloadAtom});
    setLoading(true)
    singleJob ? singleJobFlow([bannerImage], name) : uploadCSVpayload([bannerImage], [], name)
  }

  //project upload
  const projectHandler = () => {
    let eventPayload = {
      getSegmentPayloadAtom
    }
    if (fileUpload.bulkUplodAIType) {
      let homePageFeature = fileUpload?.bulkUplodAIType == 'BULK_UPLOAD_GENAI' ? 'product in context'
      : fileUpload?.bulkUplodAIType == 'BULK_UPLOAD_FACE_SWAP' ? 'model swap'
      : 'model generation';
      eventPayload = {
        homePageFeature,
        ...getSegmentPayloadAtom
      }
    }
    TrackWebappEvent(`${process.env.NEXT_PUBLIC_EVENT_TRACK_TYPE}${EventTrackName._Click_Upload_Existing_Project}`, eventPayload);
    setOpenExistingProjects(true);
  }

  const loginHandler = () => {
    TrackWebappEvent(`${process.env.NEXT_PUBLIC_EVENT_TRACK_TYPE}${EventTrackName._Click_Upload_create_free_account}`, {...getSegmentPayloadAtom});
    setLoginPopupAtom(true); 
  }
  
  const handleUploadBatchCSV = () => {
     if(!isBulkAIType || isExternalUser)return;
     setBatchCSVUploadJourney(true);
  };

  return (
    <>
    {aiTaskDisable || isBulkAIType ? <>
    { (isLoading || isRedirectUrl) ? <div style={{
      display: "flex",
      flexDirection: "column",
      gap: "16px",
      width: `${openExistingProjects ? "80%" :"100%"}`,
      opacity: isLoading ? 0.5 : 1,
      pointerEvents: isLoading ? "none" : "auto",
      minHeight: '550px', 
      justifyContent: 'center', 
      alignItems: 'center', 
  }}>
          <CircularProgress color="primary" />
      </div> : <div style={{
            display: "flex",
            flexDirection: "column",
            gap: "16px",
            width: "100%",
            opacity: isRedirectUrl ? 0.5 : 1,
            pointerEvents: isRedirectUrl ? "none" : "auto",
        }}>
          {/* {!isRedirectUrl && <div style={Styles.loaderSkelton(isExternalUser)}><CircularProgress size={24} color="secondary" /> Loading...</div>} */}
            {!openExistingProjects && <div style={styles.innerContainer}>
              <Box>{CreateProjectData?.FileFormatText || defaultStrapiData?.FileFormatTextWeb}</Box>
              <Box
                display="flex"
                gap="10px"
              >
                <Box sx={{...styles.primaryUploadCTA(false)}} {...getRootProps()} >
                  <ImageUploadButton
                    captionText={CreateProjectData?.FileUploadText || defaultStrapiData?.FileUploadText}
                    iconImageUrl={CreateProjectData?.FileUploadIcon || defaultStrapiData?.FileUploadIcon}
                    subscript={CreateProjectData?.ChooseFileText || defaultStrapiData?.FileUploadChooseText}
                    btnStyle={{background: "#F9F9F9", margin: "0px 40px"}}
                  >
                    <input {...getInputProps()} />
                  </ImageUploadButton>
                </Box>
                <Box sx={styles.primaryUploadCTA((guestUser || isExternalUser))} >
                  <ImageUploadButton
                  btnStyle={{background: "#F9F9F9", margin: "0px 40px"}}
                    captionText={CreateProjectData?.FolderUploadText || defaultStrapiData?.FolderUploadText}
                    onClick={handleButtonClick}
                    subscript={CreateProjectData?.ChooseFolderText || defaultStrapiData?.FoldUploadChooseText}
                    iconImageUrl={CreateProjectData?.FolderIcon || defaultStrapiData?.FolderIcon} />
                  <input
                    webkitdirectory="true"
                    ref={uploadInputEl}
                    directory="true"
                    multiple
                    style={{
                      opacity: 0,
                      width: "10%",
                      height: "10%",
                      position: "absolute",
                    }}
                    type="file"
                    accept="image/jpeg, image/jpg, image/png"
                    onChange={onFolderSelectBulkUpload}
                    disabled={isExternalUser}
                  /></Box>
              </Box>

              {/* Temporarily disabling Google Drive Upload */}
              <Box
                sx={styles.primaryUploadCTA(guestUser || isBulkAIType)}
              >
                <ImageUploadButton
                  width="100%"
                  onClick={handleGoogleDriveUpload}
                  titleText={CreateProjectData?.GoogleDriveText || "Google Drive"}
                  iconImageUrl={CreateProjectData?.GoogleDriveIcon || "/icons/google-drive-icon.svg"}
                  btnStyle={{backgroundColor: "#F9F9F9"}}
                />
              </Box>

              {
                !guestUser ? <><Box
                  onClick={projectHandler}
                  sx={styles.primaryUploadCTA(guestUser)}
                >
                  <ImageUploadButton
                    titleText={CreateProjectData?.existingProjectText || "Select from existing projects"}
                    iconImageUrl={ICONS.myWorkDark}
                    btnStyle={{backgroundColor: "#F9F9F9"}}
                  >
                  </ImageUploadButton>
                </Box>
                <div style={{display:"flex", flex: "1", gap:"5px", padding: "5px", border: "1px solid #EEEEEE", borderRadius: "8px",opacity:isExternalUser?0.3:1 }}>
                  {!isBulkAIType ? <Box
                  flex={0.7}
                  sx={{...styles.secondaryUploadCTA, padding: "15px"}}
                    {...(getRootPropsCSV())}
                  >
                    <input {...getInputPropsCSV()} ref={dropzoneRef} />
                    <ImageUploadButton
                      titleText={CreateProjectData?.CsvFileText || "CSV File"}
                      iconImageUrl={CreateProjectData?.CsvFileIcon || "/icons/csvlogo.svg"}
                      btnStyle={{backgroundColor: "#F9F9F9"}}
                    >
                    </ImageUploadButton>
                  </Box> :
                  <Box
                  flex={0.7}
                  sx={{...styles.secondaryUploadCTA, padding: "15px"}}
                  >
                    <ImageUploadButton
                      titleText={CreateProjectData?.CsvFileText || "CSV File"}
                      iconImageUrl={CreateProjectData?.CsvFileIcon || "/icons/csvlogo.svg"}
                      btnStyle={{backgroundColor: "#F9F9F9"}}
                      onClick={handleUploadBatchCSV}
                    >
                    </ImageUploadButton>
                  </Box>}
                  <Box
                sx={styles.secondaryUploadCTA}
                flex={0.3}
              >
                <ImageUploadButton
                
                  onClick={downloadSampleCSV}
                  captionText={CreateProjectData?.SampleText || "Sample CSV"}
                  btnStyle={{backgroundColor: "#F9F9F9", display: "flex", flexDirection: "column"}}
                  iconImageUrl={"/icons/download.svg"}
                />
              </Box>
                </div>
                  </> : <div style={{ display: "flex", flexDirection: "column", gap: "10px", marginTop: "10px", fontSize: "14px" }}>
                  <div style={{ display: "flex", justifyContent: "center" }}>
                    {entireStrapiData?.createMultipleImages || "Create multiple images at once "}
                  </div>
                  <div style={{display: "flex", justifyContent: "center"}}>
                    <div style={{ display: "flex", justifyContent: "center", background: "linear-gradient(93.62deg, rgba(240, 55, 80, 0.05) -2.75%, rgba(235, 46, 131, 0.05) 86.11%)",color:"RGB(238, 51, 106)", cursor: "pointer", width:"fit-content", padding: "10px", borderRadius: "10px" }} onClick={loginHandler}>
                      {entireStrapiData?.createFreeAccount || "Create a Free Account"}
                    </div>
                  </div>
                </div>
              }

            </div>}
            {!isExternalUser && <Box sx={styles.dummyContainer}>
            <Box width={"140px"}>{entireStrapiData?.tryDummyText || "Don't have a photo? Try one of these"}</Box>
              {dummyJobs?.map((job) => (
                  <div css={styles.dummyJobs}>
                    <img style={{width:"60px", objectFit: "contain"}}src={job?.bgRemovedBannerImage} onClick={()=>{dummyJobUpload(job?.name, job?.bgRemovedBannerImage, false)}} crossorigin="anonymous"/>
                  </div>
                ))}
            </Box>}
            {(csvUploading && openExistingProjects) ? <div style={{
            display: "flex",
            flexDirection: "column",
            width:'100%',
            height:'300px',
            backgroundColor:'#fff',
            opacity: 1,
            pointerEvents: "none",
            justifyContent: 'center', 
            alignItems: 'center', 
        }}>
          <CircularProgress color="primary" />
          </div> : <ExistingProjectsListPopup
              openExistingProjects={openExistingProjects}
              backHandler={() => setOpenExistingProjects(false)}
              batchEditorJobFromExistingImages={batchEditorJobFromExistingImages}
              homePageBatchEditorFlow={true}
              isBulkAIType={isBulkAIType}
              onExistingProjectHandler={onExistingProjectHandler}
            />}
          </div>
      }
      {batchCSVUploadJourney && <CSVUploadJourney setBatchCSVUploadJourney={setBatchCSVUploadJourney} isBulkAIType={isBulkAIType} />}
      <BatchEditorSuccessDialog
      open={openFileUploadSuccessDialog}
      onClose={()=>{
        setOpenFileUploadSuccessDialog(false);
        setLoading(false);
      }}
      isBulkAIType={isBulkAIType}
      saveProject={selectionOptionHandler}
    />
    <SelectGenerationModel
      open={openSelectionDialog}
      onClose={()=>setOpenSelectionDialog(false)}
      saveProject={saveProject}
      applying={csvUploading}
       />
    </> : <>
    { isLoading ? <div style={{
      display: "flex",
      flexDirection: "column",
      gap: "16px",
      width: `${openExistingProjects ? "80%" :"100%"}`,
      opacity: isLoading ? 0.5 : 1,
      pointerEvents: isLoading ? "none" : "auto",
      minHeight: '550px', 
      justifyContent: 'center', 
      alignItems: 'center', 
  }}>
          <CircularProgress color="primary" />
      </div> : <div style={{
            display: "flex",
            flexDirection: "column",
            gap: "16px",
            width: "100%",
            opacity: isRedirectUrl ? 0.5 : 1,
            pointerEvents: isRedirectUrl ? "none" : "auto",
        }}>
          {isRedirectUrl && <div style={Styles.loaderSkelton(isExternalUser)}><CircularProgress size={24} color="secondary" /> Loading...</div>}
            {!openExistingProjects && <div style={styles.innerContainer}>
              <Box>{CreateProjectData?.FileFormatText || defaultStrapiData?.FileFormatTextWeb}</Box>
              <Box
                display="flex"
                gap="10px"
              >
                <Box sx={styles.FileUploadCTA} {...getRootProps()} >
                  <ImageUploadButton
                    captionText={CreateProjectData?.FileUploadText || defaultStrapiData?.FileUploadText}
                    iconImageUrl={CreateProjectData?.FileUploadIcon || defaultStrapiData?.FileUploadIcon}
                    btnStyle={{background: "#F9F9F9", margin: "0px 40px"}}
                  >
                    <input {...getInputProps()} />
                  </ImageUploadButton>
                </Box>         
              </Box>

              {
                !guestUser ? <Box
                  onClick={projectHandler}
                  sx={styles.primaryUploadCTA(guestUser)}
                >
                  <ImageUploadButton
                    captionText={CreateProjectData?.existingProjectText || "Select from existing projects"}
                    iconImageUrl={ICONS.myWorkDark}
                    btnStyle={{backgroundColor: "#F9F9F9"}}
                  >
                  </ImageUploadButton>
                </Box> : <div style={{ display: "flex", flexDirection: "column", gap: "10px", marginTop: "10px", fontSize: "14px" }}>
                  <div style={{ display: "flex", justifyContent: "center" }}>
                    {entireStrapiData?.createMultipleImages || "Create multiple images at once "}
                  </div>
                  <div style={{display: "flex", justifyContent: "center"}}>
                    <div style={{ display: "flex", justifyContent: "center", background: "linear-gradient(93.62deg, rgba(240, 55, 80, 0.05) -2.75%, rgba(235, 46, 131, 0.05) 86.11%)",color:"RGB(238, 51, 106)", cursor: "pointer", width:"fit-content", padding: "10px", borderRadius: "10px" }} onClick={loginHandler}>
                      {entireStrapiData?.createFreeAccount || "Create a Free Account"}
                    </div>
                  </div>
                </div>
              }

            </div>}
            {!isExternalUser && <Box sx={styles.dummyContainer}>
            <Box width={"140px"}>{entireStrapiData?.tryDummyText || "Don't have a photo? Try one of these"}</Box>
              {dummyJobs?.map((job) => (
                  <div css={styles.dummyJobs}>
                    <img style={{width:"60px", objectFit: "contain"}}src={job?.bgRemovedBannerImage} onClick={()=>{dummyJobUpload(job?.name, job?.bgRemovedBannerImage, true)}} crossorigin="anonymous"/>
                  </div>
                ))}
            </Box>}
            {(csvUploading && openExistingProjects) ? <div style={{
            display: "flex",
            flexDirection: "column",
            width:'100%',
            height:'300px',
            backgroundColor:'#fff',
            opacity: 1,
            pointerEvents: "none",
            justifyContent: 'center', 
            alignItems: 'center', 
        }}>
          <CircularProgress color="primary" />
          </div> : <ExistingProjectsListPopup
              openExistingProjects={openExistingProjects}
              backHandler={() => setOpenExistingProjects(false)}
              batchEditorJobFromExistingImages={singleJobFromExistingImages}
              homePageBatchEditorFlow={true}
              isBulkAIType={isBulkAIType}
              onExistingProjectHandler={onExistingProjectHandler}
            />}
          </div>
      }
      {batchCSVUploadJourney && <CSVUploadJourney setBatchCSVUploadJourney={setBatchCSVUploadJourney} isBulkAIType={isBulkAIType} />}
      <BatchEditorSuccessDialog
      open={openFileUploadSuccessDialog}
      onClose={()=>{
        setOpenFileUploadSuccessDialog(false);
        setLoading(false);
      }}
      isBulkAIType={isBulkAIType}
      saveProject={selectionOptionHandler}
    />
    <SelectGenerationModel
      open={openSelectionDialog}
      onClose={()=>setOpenSelectionDialog(false)}
      saveProject={saveProject}
      applying={csvUploading}
       />
     
    </>}
    </>
  );
};

const Styles={
  loaderSkelton:(externalUser)=>({
    position:'absolute',
    width:'100%',
    height:externalUser ? '475px' : '571px',
    display:'flex',
    justifyContent:'center',
    alignItems:'center',
    zIndex:9999,
    gap:'10px',
    opacity:0.5,
    background:"transparent",
  })
}
export default HomepageUpload;
